import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import { theme } from "../../../theme";
import BodySmall from "../../atoms/typography/BodySmall";
import Small from "../../atoms/typography/Small";
import ChipTag from "../../atoms/ChipTag";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import TableRow from "../../atoms/TableRow";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { PERMISSION_CATEGORIES } from "../../../models/permissionLabels";
import type {
  SkynetUserGroupMembership,
  SkynetUserGroupPermissions,
} from "../../../models/RolesAndPermissionsModels";

interface Props {
  groups: Array<SkynetUserGroupMembership>;
  // "granted" shows what a group hands its members (defaultRead/defaultWrite),
  // "max" shows what a group admin is allowed to hand out (maxRead/maxWrite).
  variant: "granted" | "max";
  emptyText?: string;
}

const GroupCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${theme.colors.primary20};
  padding: 16px;
`;

const CategoryRow = styled(Row)`
  background-color: ${theme.colors.bgSubtle};
  padding: 4px 8px;
`;

const GroupPermissionsCards: FC<Props> = ({ groups, variant, emptyText }) => {
  const { t } = useTranslation();
  const [openGroupIds, setOpenGroupIds] = useState<Array<string>>([]);

  const toggleGroupOpen = (id: string) => {
    setOpenGroupIds((prev) =>
      prev.includes(id)
        ? prev.filter((openId) => openId !== id)
        : [...prev, id],
    );
  };

  const permissionSets = (
    permissions: SkynetUserGroupPermissions,
    key: (typeof PERMISSION_CATEGORIES)[number]["key"],
  ) => {
    const sets = permissions[key];
    return variant === "max"
      ? { read: sets?.maxRead ?? [], write: sets?.maxWrite ?? [] }
      : { read: sets?.defaultRead ?? [], write: sets?.defaultWrite ?? [] };
  };

  return (
    <Row>
      {groups.map((membership) => {
        const permissions = membership.group?.permissions;
        const isOpen = openGroupIds.includes(membership.id);

        const categories = permissions
          ? PERMISSION_CATEGORIES.map((category) => {
              const { read, write } = permissionSets(permissions, category.key);
              return {
                ...category,
                read,
                write,
                perms: [...new Set([...read, ...write])],
              };
            }).filter((category) => category.perms.length > 0)
          : [];

        return (
          <Col lg={6} key={membership.id} style={{ marginBottom: 8 }}>
            <GroupCard>
              <Row nogutter justify="between" align="center">
                <ChipTag tagTitleKey={membership.groupName} size="large" />
                <ChevronButton
                  type={isOpen ? "up" : "down"}
                  onClick={() => toggleGroupOpen(membership.id)}
                />
              </Row>
              <SlideOutHeight isVisible={isOpen}>
                <div style={{ paddingTop: 8 }}>
                  {categories.length === 0 && (
                    <BodySmall>
                      {emptyText ?? t("users.noPermissionsFromGroup")}
                    </BodySmall>
                  )}
                  {categories.map((category) => (
                    <div key={category.key}>
                      <CategoryRow justify="between" align="center" nogutter>
                        <BodySmall fontweight="500">
                          {t(category.titleKey)}
                        </BodySmall>
                      </CategoryRow>
                      {category.perms.map((perm) => (
                        <TableRow
                          key={perm}
                          style={{ padding: 4 }}
                          clickable={false}
                        >
                          <Col lg={6}>
                            <Small fontweight={400}>
                              {category.labels[perm]
                                ? t(category.labels[perm])
                                : perm}
                            </Small>
                          </Col>
                          <Col
                            lg={3}
                            style={{
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            {category.read.includes(perm) ? (
                              <CheckmarkIconSmall />
                            ) : (
                              <CrossIcon />
                            )}
                            <Small fontweight={400}>{t("users.read")}</Small>
                          </Col>
                          <Col
                            lg={3}
                            style={{
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            {category.write.includes(perm) ? (
                              <CheckmarkIconSmall />
                            ) : (
                              <CrossIcon />
                            )}
                            <Small fontweight={400}>{t("users.write")}</Small>
                          </Col>
                        </TableRow>
                      ))}
                    </div>
                  ))}
                </div>
              </SlideOutHeight>
            </GroupCard>
          </Col>
        );
      })}
    </Row>
  );
};

export default GroupPermissionsCards;
